import { useRef, useState } from 'react';
import { Download, FileText, Loader2, Paperclip, X } from 'lucide-react';
import { cn, formatBytes } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { attachmentsApi, type FormAnswers, type FormField, type FormResponseAttachment } from '@/lib/api';
import { formatAnswer, isBlank } from './form-utils';

export interface FormRendererProps {
  fields: FormField[];
  answers: FormAnswers;
  onChange?: (fieldId: string, value: unknown) => void;
  /** Read-only: answers are printed, not editable. */
  readOnly?: boolean;
  errors?: Record<string, string>;
  /** Needed for file fields in edit mode. */
  upload?: {
    organizationId: string;
    responseId: string;
    attachments: FormResponseAttachment[];
    onAttachmentsChange: (next: FormResponseAttachment[]) => void;
  };
  /** Builder preview: inputs render but are inert. */
  preview?: boolean;
}

/**
 * The one place a FormField becomes an input. Used by the fill page, the
 * builder's preview, and the staff response view (read-only).
 */
export function FormRenderer({ fields, answers, onChange, readOnly, errors, upload, preview }: FormRendererProps) {
  const fileNames = new Map((upload?.attachments ?? []).map((a) => [a.id, a.fileName]));
  const disabled = !!preview;
  const set = (id: string, v: unknown) => onChange?.(id, v);

  return (
    <div className="space-y-6">
      {fields.map((field) => {
        if (field.type === 'section') {
          return (
            <div key={field.id} className="pt-2 border-t border-grey-100 first:border-t-0 first:pt-0">
              <h3 className="text-heading-3 text-secondary">{field.label}</h3>
              {field.helpText && <p className="text-sm text-grey-500 mt-1 whitespace-pre-line">{field.helpText}</p>}
            </div>
          );
        }

        const value = answers[field.id];
        const error = errors?.[field.id];
        const inputId = `field-${field.id}`;
        const describedBy = field.helpText ? `${inputId}-help` : undefined;

        return (
          <div key={field.id}>
            <label htmlFor={inputId} className="block text-sm font-semibold text-secondary mb-1.5">
              {field.label}
              {field.required && !readOnly && <span className="text-error ml-1" aria-hidden>*</span>}
            </label>
            {field.helpText && (
              <p id={describedBy} className="text-xs text-grey-500 mb-2 whitespace-pre-line">
                {field.helpText}
              </p>
            )}

            {readOnly ? (
              <ReadOnlyAnswer field={field} value={value} attachments={upload?.attachments ?? []} fileNames={fileNames} />
            ) : (
              <FieldInput
                field={field}
                value={value}
                inputId={inputId}
                describedBy={describedBy}
                disabled={disabled}
                invalid={!!error}
                onChange={(v) => set(field.id, v)}
                upload={upload}
              />
            )}

            {error && (
              <p className="text-xs text-error mt-1.5" role="alert">
                {error}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FieldInput({
  field,
  value,
  inputId,
  describedBy,
  disabled,
  invalid,
  onChange,
  upload,
}: {
  field: FormField;
  value: unknown;
  inputId: string;
  describedBy?: string;
  disabled: boolean;
  invalid: boolean;
  onChange: (v: unknown) => void;
  upload?: FormRendererProps['upload'];
}) {
  const inputClass = cn('input', invalid && 'input-error');
  const common = { id: inputId, 'aria-describedby': describedBy, 'aria-invalid': invalid || undefined, disabled };

  switch (field.type) {
    case 'short_text':
      return <input {...common} type="text" className={inputClass} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />;
    case 'email':
      return <input {...common} type="email" className={inputClass} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} placeholder="name@example.com" />;
    case 'url':
      return <input {...common} type="url" className={inputClass} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} placeholder="https://" />;
    case 'long_text':
      return <textarea {...common} rows={4} className={inputClass} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />;
    case 'number':
      return (
        <input
          {...common}
          type="number"
          inputMode="decimal"
          className={cn(inputClass, 'max-w-xs')}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        />
      );
    case 'date':
      return (
        <input {...common} type="date" className={cn(inputClass, 'max-w-xs')} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value || undefined)} />
      );
    case 'yes_no':
      return (
        <div role="radiogroup" aria-labelledby={inputId} className="flex gap-2">
          {[
            { label: 'Yes', v: true },
            { label: 'No', v: false },
          ].map((opt) => (
            <button
              key={opt.label}
              type="button"
              role="radio"
              aria-checked={value === opt.v}
              disabled={disabled}
              onClick={() => onChange(value === opt.v ? undefined : opt.v)}
              className={cn(
                'px-4 py-2 rounded-lg border text-sm font-medium transition-colors',
                value === opt.v ? 'border-accent bg-accent/10 text-accent' : 'border-grey-200 text-grey-600 hover:border-grey-300',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );
    case 'single_choice':
      return (
        <div role="radiogroup" aria-labelledby={inputId} className="space-y-2">
          {(field.options ?? []).map((opt) => (
            <label key={opt} className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name={inputId}
                disabled={disabled}
                checked={value === opt}
                onChange={() => onChange(opt)}
                className="w-4 h-4 text-accent border-grey-300 focus:ring-accent"
              />
              <span className="text-sm text-secondary">{opt}</span>
            </label>
          ))}
        </div>
      );
    case 'multiple_choice': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div role="group" aria-labelledby={inputId} className="space-y-2">
          {(field.options ?? []).map((opt) => {
            const checked = selected.includes(opt);
            return (
              <label key={opt} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={checked}
                  onChange={() => onChange(checked ? selected.filter((s) => s !== opt) : [...selected, opt])}
                  className="w-4 h-4 rounded text-accent border-grey-300 focus:ring-accent"
                />
                <span className="text-sm text-secondary">{opt}</span>
              </label>
            );
          })}
        </div>
      );
    }
    case 'file':
      return <FileUploadField field={field} value={value} inputId={inputId} disabled={disabled} onChange={onChange} upload={upload} />;
    default:
      return null;
  }
}

function FileUploadField({
  field,
  value,
  inputId,
  disabled,
  onChange,
  upload,
}: {
  field: FormField;
  value: unknown;
  inputId: string;
  disabled: boolean;
  onChange: (v: unknown) => void;
  upload?: FormRendererProps['upload'];
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<Array<{ name: string; progress: number }>>([]);
  const [error, setError] = useState<string | null>(null);
  const ids = Array.isArray(value) ? (value as string[]) : [];
  const max = field.maxFiles ?? 5;
  const byId = new Map((upload?.attachments ?? []).map((a) => [a.id, a]));
  const files = ids.map((id) => byId.get(id)).filter(Boolean) as FormResponseAttachment[];
  const canAdd = !disabled && !!upload && ids.length < max;

  const handleFiles = async (list: FileList | null) => {
    if (!list || !upload) return;
    const room = max - ids.length;
    const picked = Array.from(list).slice(0, room);
    if (picked.length === 0) return;
    setError(null);
    let nextIds = [...ids];
    let nextAttachments = [...upload.attachments];
    for (const file of picked) {
      setUploading((u) => [...u, { name: file.name, progress: 0 }]);
      try {
        const { attachmentId } = await attachmentsApi.uploadFile(file, upload.organizationId, 'formResponse', upload.responseId, (p) =>
          setUploading((u) => u.map((x) => (x.name === file.name ? { ...x, progress: p } : x))),
        );
        nextIds = [...nextIds, attachmentId];
        nextAttachments = [...nextAttachments, { id: attachmentId, fileName: file.name, fileSize: file.size, mimeType: file.type }];
        upload.onAttachmentsChange(nextAttachments);
        onChange(nextIds);
      } catch (e: any) {
        setError(e?.message || `Couldn't upload ${file.name}`);
      } finally {
        setUploading((u) => u.filter((x) => x.name !== file.name));
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const remove = async (id: string) => {
    if (!upload) return;
    try {
      await attachmentsApi.delete(id);
    } catch (e: any) {
      setError(e?.message || "Couldn't remove the file");
      return;
    }
    onChange(ids.filter((x) => x !== id));
    upload.onAttachmentsChange(upload.attachments.filter((a) => a.id !== id));
  };

  return (
    <div className="space-y-2">
      {files.map((f) => (
        <FileRow key={f.id} file={f} onRemove={disabled ? undefined : () => remove(f.id)} />
      ))}
      {uploading.map((u) => (
        <div key={u.name} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-grey-100 bg-grey-50 text-sm text-grey-600">
          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          <span className="truncate flex-1">{u.name}</span>
          <span className="text-xs tabular-nums">{u.progress}%</span>
        </div>
      ))}
      {canAdd && (
        <>
          <input ref={inputRef} id={inputId} type="file" multiple={max > 1} className="sr-only" onChange={(e) => handleFiles(e.target.files)} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-grey-300 text-sm text-grey-600 hover:border-accent hover:text-accent transition-colors"
          >
            <Paperclip className="w-4 h-4" />
            {ids.length ? 'Add another file' : 'Choose a file'}
            <span className="text-xs text-grey-400">
              ({ids.length}/{max})
            </span>
          </button>
        </>
      )}
      {disabled && files.length === 0 && (
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-grey-200 text-sm text-grey-400">
          <Paperclip className="w-4 h-4" /> File upload
        </div>
      )}
      {error && (
        <p className="text-xs text-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

async function downloadAttachment(file: FormResponseAttachment) {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(attachmentsApi.getServerDownloadUrl(file.id), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function FileRow({ file, onRemove }: { file: FormResponseAttachment; onRemove?: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-grey-100 bg-white text-sm">
      <FileText className="w-4 h-4 text-grey-400 shrink-0" />
      <span className="truncate flex-1 text-secondary">{file.fileName}</span>
      <span className="text-xs text-grey-400 tabular-nums shrink-0">{formatBytes(file.fileSize)}</span>
      <button
        type="button"
        onClick={async () => {
          setBusy(true);
          try {
            await downloadAttachment(file);
          } finally {
            setBusy(false);
          }
        }}
        className="p-1 text-grey-400 hover:text-secondary transition-colors"
        aria-label={`Download ${file.fileName}`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      </button>
      {onRemove && (
        <button type="button" onClick={onRemove} className="p-1 text-grey-400 hover:text-error transition-colors" aria-label={`Remove ${file.fileName}`}>
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function ReadOnlyAnswer({
  field,
  value,
  attachments,
  fileNames,
}: {
  field: FormField;
  value: unknown;
  attachments: FormResponseAttachment[];
  fileNames: Map<string, string>;
}) {
  if (field.type === 'file') {
    const ids = Array.isArray(value) ? (value as string[]) : [];
    const byId = new Map(attachments.map((a) => [a.id, a]));
    const files = ids.map((id) => byId.get(id)).filter(Boolean) as FormResponseAttachment[];
    if (files.length === 0) return <p className="text-sm text-grey-400">—</p>;
    return (
      <div className="space-y-2">
        {files.map((f) => (
          <FileRow key={f.id} file={f} />
        ))}
      </div>
    );
  }
  const text = formatAnswer(field, value, fileNames);
  return (
    <p className={cn('text-sm whitespace-pre-wrap break-words', isBlank(value) ? 'text-grey-400' : 'text-secondary')}>
      {field.type === 'url' && !isBlank(value) ? (
        <a href={String(value)} target="_blank" rel="noreferrer" className="text-accent hover:underline">
          {text}
        </a>
      ) : (
        text
      )}
    </p>
  );
}
